import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ShoppingBag,
  Clock,
  Accessibility,
  CreditCard,
  Users,
  Truck,
  Phone,
  CheckCircle,
  Star,
  Heart,
  Shield,
  Banknote,
  Smartphone,
  Wifi
} from 'lucide-react';
import { services, paymentMethods } from '../mock';

const Services = () => {
  const serviceIcons = {
    ShoppingBag: ShoppingBag,
    Clock: Clock,
    Accessibility: Accessibility,
    CreditCard: CreditCard,
    Users: Users,
    Truck: Truck
  };

  const paymentIcons = {
    CreditCard: CreditCard,
    Banknote: Banknote,
    Smartphone: Smartphone,
    Wifi: Wifi
  };

  const additionalServices = [
    {
      title: "Consultation Pharmaceutique",
      description: "Rendez-vous personnalisé avec nos pharmaciens",
      features: ["Bilan médicamenteux", "Conseils d'usage", "Interactions médicamenteuses"]
    },
    {
      title: "Matériel Médical",
      description: "Large gamme d'équipements médicaux et orthopédiques",
      features: ["Location d'équipements", "Conseils d'utilisation", "Service après-vente"]
    },
    {
      title: "Préparations Magistrales",
      description: "Préparations personnalisées selon prescriptions",
      features: ["Formulations uniques", "Dosages adaptés", "Contrôle qualité"]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                Nos <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">Services</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Découvrez notre gamme complète de services pharmaceutiques conçus pour répondre 
                à tous vos besoins de santé avec professionnalisme et modernité.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <Star className="h-8 w-8 text-yellow-500 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900">4.8/5</div>
                <div className="text-sm text-gray-600">Note clients</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <Heart className="h-8 w-8 text-red-500 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900">1000+</div>
                <div className="text-sm text-gray-600">Clients satisfaits</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <Shield className="h-8 w-8 text-teal-600 mx-auto mb-3" />
                <div className="text-2xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-600">Sécurisé</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Services Principaux
            </h2>
            <p className="text-xl text-gray-600">
              Des services modernes pour votre commodité
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = serviceIcons[service.icon];
              return (
                <Card key={service.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl p-4 w-16 h-16 mb-6 flex items-center justify-center">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-teal-600" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Modes de Paiement
            </h2>
            <p className="text-xl text-gray-600">
              Payez comme vous le souhaitez, en toute sécurité
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {paymentMethods.map((method, index) => {
              const IconComponent = paymentIcons[method.icon];
              return (
                <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900">
                      {method.name}
                    </h3>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Paiement Sécurisé Garanti
                </h3>
                <p className="text-gray-600 mb-6">
                  Toutes vos transactions sont protégées par des systèmes de sécurité avancés. 
                  Nous acceptons tous les modes de paiement modernes pour votre commodité.
                </p>
                <div className="flex items-center space-x-2 text-teal-600">
                  <Shield className="h-5 w-5" />
                  <span className="font-medium">Certification PCI DSS</span>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl p-8">
                  <CreditCard className="h-16 w-16 text-teal-600 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900">
                    Paiement Sans Contact
                  </p>
                  <p className="text-sm text-gray-600">
                    Rapide et sécurisé
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Services Spécialisés
            </h2>
            <p className="text-xl text-gray-600">
              Expertise pharmaceutique avancée
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="bg-teal-100 rounded-full p-1">
                          <CheckCircle className="h-4 w-4 text-teal-600" />
                        </div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              Besoin d'Informations ?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Nos pharmaciens sont à votre disposition pour répondre à toutes vos questions 
              et vous conseiller selon vos besoins spécifiques.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="bg-white text-teal-600 hover:bg-gray-50 px-8 py-4">
                <Phone className="h-5 w-5 mr-2" />
                Nous Appeler
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4">
                <Users className="h-5 w-5 mr-2" />
                Prendre RDV
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;